import React, { useRef, useState } from 'react';

import { toastWarning } from '@components/Toast';

import { validateFloatNumber } from '@utils/Util';

const BOTH_FIELDS_WARNING = 'Please clear one field before entering a value in the other.';

const hasValue = (value) => value !== null && value !== undefined && value !== '';
// the API stores a cleared field as 0, so 0 is treated as "not given"
const hasNonZeroValue = (value) => hasValue(value) && Number(value) !== 0;

/** Offer price suffix: `($1.2)` when the margin was given as an amount, otherwise `(2%)` */
export const getMarginLabel = (margin, marginValue) => {
	if (hasNonZeroValue(marginValue)) return `($${marginValue})`;
	if (margin >= 0) return `(${margin ?? ''}%)`;
	return null;
};

/**
 * Offer price editor with two fields: margin percentage or a direct margin amount.
 * Only one of them can have a value, the other field stays locked until it is cleared
 * and warns the user when they try to use it.
 * Sends `margin` for the percentage (as before) or `marginValue` for the amount,
 * an emptied field is sent as 0.
 * Saves when the focus leaves the whole editor or on Enter.
 */
const OfferMarginInput = ({ margin, marginValue, disabled = false, onSave, onCancel }) => {
	const initialAmount = hasNonZeroValue(marginValue) ? marginValue : '';
	// a 0 margin means no margin was given, so the field starts empty
	const initialMargin = initialAmount === '' && hasNonZeroValue(margin) ? margin : '';
	const [percentage, setPercentage] = useState(initialMargin);
	const [amount, setAmount] = useState(initialAmount);
	const isSubmitted = useRef(false);

	// a locked field stays read only (not disabled) so it can still react to the user's click
	const isPercentageLocked = amount !== '';
	const isAmountLocked = percentage !== '';

	const onLockedFieldAttempt = (isLocked) => (event) => {
		if (!isLocked || disabled) return;
		if (event.type === 'mousedown') event.preventDefault();
		toastWarning(BOTH_FIELDS_WARNING);
	};

	const submit = async () => {
		// Enter followed by the blur of the closing editor must not save twice
		if (isSubmitted.current) return;
		isSubmitted.current = true;

		try {
			if (String(amount) !== String(initialAmount) && (amount !== '' || percentage === '')) {
				// a new amount, or a saved amount that was cleared
				await onSave({ marginValue: parseFloat(amount) || 0 });
			} else if (String(percentage) !== String(initialMargin)) {
				await onSave({ margin: parseFloat(percentage) || 0 });
			} else {
				onCancel();
			}
		} finally {
			// allow a retry when the editor stays open (e.g. the update failed)
			isSubmitted.current = false;
		}
	};

	const onBlurEditor = (event) => {
		// moving between the two fields must not close the editor
		if (event.currentTarget.contains(event.relatedTarget)) return;
		submit();
	};

	const onKeyDown = (event) => {
		if (event.key === 'Enter') submit();
	};

	return (
		<div className="offer-margin-inputs" onBlur={onBlurEditor}>
			<div className={`offer-margin-input-wrapper ${isPercentageLocked ? 'locked' : ''}`}>
				<span className="offer-margin-prefix">%</span>
				<input
					type="number"
					autoComplete="off"
					name="margin"
					placeholder="Percentage"
					value={percentage}
					autoFocus={initialAmount === ''}
					disabled={disabled}
					readOnly={isPercentageLocked}
					onMouseDown={onLockedFieldAttempt(isPercentageLocked)}
					onFocus={onLockedFieldAttempt(isPercentageLocked)}
					onKeyPress={validateFloatNumber}
					onKeyDown={onKeyDown}
					onChange={(event) => setPercentage(event.target.value)}
				/>
			</div>
			<span className="gray_dark_02 regular-12">or</span>
			<div className={`offer-margin-input-wrapper ${isAmountLocked ? 'locked' : ''}`}>
				<span className="offer-margin-prefix">$</span>
				<input
					type="number"
					autoComplete="off"
					name="marginValue"
					placeholder="Amount"
					value={amount}
					autoFocus={initialAmount !== ''}
					disabled={disabled}
					readOnly={isAmountLocked}
					onMouseDown={onLockedFieldAttempt(isAmountLocked)}
					onFocus={onLockedFieldAttempt(isAmountLocked)}
					onKeyPress={validateFloatNumber}
					onKeyDown={onKeyDown}
					onChange={(event) => setAmount(event.target.value)}
				/>
			</div>
		</div>
	);
};

export default OfferMarginInput;
